(() => {
  const roots = document.querySelectorAll('[data-fs-live-root]');
  roots.forEach((root) => { void init(root); });

  async function init(root) {
    const state = {
      event: null,
      schedule: [],
      contestants: [],
      staff: [],
      messages: [],
      scoreboard: [],
      scoreConfig: { mode: 'points_high', unit: 'points', target: null, attempts: 1, judges: 1, drop_high_low: false, penalties_enabled: true, tiebreak: 'best_attempt' },
      clockSeconds: 0,
      clockTimer: null,
      supabase: null
    };

    const q = (s) => root.querySelector(s);
    const qa = (s) => [...root.querySelectorAll(s)];
    const notice = q('[data-runtime-notice]');

    qa('[data-tab-target]').forEach((button) => button.addEventListener('click', () => {
      qa('[data-tab-target]').forEach((b) => b.classList.toggle('is-active', b === button));
      qa('[data-panel]').forEach((p) => p.classList.toggle('is-active', p.dataset.panel === button.dataset.tabTarget));
    }));

    qa('[data-field="alcohol_present"]').forEach((el) => el.addEventListener('change', () => syncConditionalPanels()));
    qa('[data-field="minors_allowed"], [data-field="guardian_required_for_minors"]').forEach((el) => el.addEventListener('change', syncConditionalPanels));

    bindAction('save-event', saveEvent);
    bindAction('add-schedule', addSchedule);
    bindAction('calculate-score', calculateDemo);
    bindAction('save-scoring', saveScoring);
    bindAction('start-clock', toggleClock);
    bindAction('reset-clock', resetClock);
    bindAction('refresh-scoreboard', refreshScoreboard);
    bindAction('pause-event', pauseEvent);
    bindAction('complete-inspection', completeInspection);
    bindAction('add-hazard', addHazard);
    bindAction('log-incident', logIncident);
    bindAction('send-message', sendMessage);
    bindAction('export-roster', () => exportCsv('fanscore-live-roster.csv', state.contestants));
    bindAction('export-results', () => exportCsv('fanscore-live-results.csv', state.scoreboard));
    bindAction('export-compliance', exportCompliance);
    bindAction('export-operations', exportOperations);
    bindAction('copy-packet-link', copyPacketLink);

    q('[data-roster-search]')?.addEventListener('input', renderRoster);

    state.supabase = await resolveSupabase(root);
    const requestedEventId = new URLSearchParams(window.location.search).get('event');
    if (!state.supabase) {
      showNotice('Supabase connection could not be initialized. Check the browser console or theme content-security settings.');
      seedPreview();
    } else {
      const connection = await verifySupabase(state.supabase);
      if (!connection.ok) {
        showNotice(`Supabase connection failed: ${connection.message}`);
        seedPreview();
      } else if (connection.user) {
        if (requestedEventId) {
          await loadEvent(requestedEventId);
        } else {
          const loaded = await loadMostRecentEvent();
          if (!loaded) {
            seedPreview();
            showNotice(`Supabase connected as ${connection.user.email || 'FanScore user'}. No live event exists yet. Open Setup, enter the event details, and click Save Event. Supabase will generate the event UUID automatically.`);
          }
        }
      } else {
        seedPreview();
        showNotice('Supabase is connected. No UUID is required. Sign in through FanScore, then use Setup → Save Event; Supabase will generate the event ID automatically.');
      }
    }

    syncConditionalPanels();
    renderAll();

    function bindAction(name, fn) { q(`[data-action="${name}"]`)?.addEventListener('click', fn); }

    async function resolveSupabase(el) {
      if (window.FanScoreSupabase?.client) return window.FanScoreSupabase.client;
      if (window.supabaseClient) return window.supabaseClient;

      const url = el.dataset.supabaseUrl;
      const key = el.dataset.supabaseKey;
      if (!url || !key) return null;

      if (!window.supabase?.createClient) {
        try { await loadSupabaseBrowserClient(); } catch (error) { console.error('[FanScore Live] Supabase library load failed', error); return null; }
      }
      if (!window.supabase?.createClient) return null;

      if (!window.__FanScoreLiveSupabaseClient) {
        window.__FanScoreLiveSupabaseClient = window.supabase.createClient(url, key, {
          auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
        });
      }
      return window.__FanScoreLiveSupabaseClient;
    }

    function loadSupabaseBrowserClient() {
      if (window.supabase?.createClient) return Promise.resolve();
      if (window.__FanScoreSupabaseJsPromise) return window.__FanScoreSupabaseJsPromise;
      window.__FanScoreSupabaseJsPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-fs-supabase-js]');
        if (existing) {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', () => reject(new Error('Supabase browser library failed to load.')), { once: true });
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2.112.3/dist/umd/supabase.min.js';
        script.defer = true;
        script.dataset.fsSupabaseJs = '2.112.3';
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', () => reject(new Error('Supabase browser library failed to load.')), { once: true });
        document.head.appendChild(script);
      });
      return window.__FanScoreSupabaseJsPromise;
    }

    async function verifySupabase(client) {
      try {
        const { error } = await client.from('fs_live_scoreboard_rows').select('id').limit(1);
        if (error) return { ok: false, message: error.message, user: null };
        const { data: sessionData } = await client.auth.getSession();
        return { ok: true, message: 'Connected', user: sessionData?.session?.user || null };
      } catch (error) {
        return { ok: false, message: error.message || 'Unknown connection error', user: null };
      }
    }

    async function loadMostRecentEvent() {
      try {
        const { data, error } = await state.supabase.from('fs_live_events').select('id').order('created_at', { ascending: false }).limit(1);
        if (error) throw error;
        if (!data?.[0]?.id) return false;
        await loadEvent(data[0].id);
        return true;
      } catch (error) {
        console.warn('[FanScore Live] Could not auto-load event', error);
        return false;
      }
    }

    async function loadEvent(id) {
      try {
        const { data: event, error } = await state.supabase.from('fs_live_events').select('*').eq('id', id).single();
        if (error) throw error;
        state.event = event;
        hydrateEventForm(event);
        const [schedule, contestants, staff, scoreboard, messages] = await Promise.all([
          state.supabase.from('fs_live_schedule_items').select('*').eq('event_id', id).order('starts_at'),
          state.supabase.from('fs_live_contestants').select('id,event_id,user_id,public_name,team_name,division_name,packet_status,check_in_status,bib_number,display_public').eq('event_id', id).order('public_name'),
          state.supabase.from('fs_live_event_staff').select('id,event_id,user_id,display_name,role,assignment,status').eq('event_id', id).order('role'),
          state.supabase.from('fs_live_scoreboard_rows').select('*').eq('event_id', id).order('rank'),
          state.supabase.from('fs_live_messages').select('id,event_id,audience_type,subject,priority,delivery_status,created_at').eq('event_id', id).order('created_at', { ascending: false }).limit(50)
        ]);
        state.schedule = schedule.data || [];
        state.contestants = contestants.data || [];
        state.staff = staff.data || [];
        state.scoreboard = scoreboard.data || [];
        state.messages = messages.data || [];
        renderAll();
        subscribeRealtime(id);
        hideNotice();
      } catch (err) { showNotice(`Could not load live event: ${err.message}`); seedPreview(); renderAll(); }
    }

    async function saveEvent() {
      const payload = readEventForm();
      if (!payload.name) return showNotice('Event name is required.');
      if (!state.supabase) { state.event = { ...(state.event || {}), ...payload, id: state.event?.id || 'preview-event' }; renderAll(); return showNotice('Preview saved locally. Connect Supabase to persist the event.'); }
      try {
        let query;
        if (state.event?.id && state.event.id !== 'preview-event') {
          query = state.supabase.from('fs_live_events').update(payload).eq('id', state.event.id).select().single();
        } else {
          const { data: authData, error: authError } = await state.supabase.auth.getUser();
          if (authError || !authData?.user?.id) throw new Error('You must be signed in to create a live event.');
          query = state.supabase.from('fs_live_events').insert({ ...payload, organizer_user_id: authData.user.id }).select().single();
        }
        const { data, error } = await query; if (error) throw error;
        state.event = data; renderAll(); hideNotice();
      } catch (err) { showNotice(`Event save failed: ${err.message}`); }
    }

    function readEventForm() {
      const value = (name) => q(`[data-field="${name}"]`)?.value || null;
      const checked = (name) => !!q(`[data-field="${name}"]`)?.checked;
      return {
        name: value('name'), slug: value('slug') || slugify(value('name') || ''), venue_name: value('venue_name'), timezone: value('timezone') || 'America/New_York',
        starts_at: toIso(value('starts_at')), ends_at: toIso(value('ends_at')), competition_format: value('competition_format') || 'individual', capacity: Number(value('capacity') || 0) || null,
        public_scoreboard: checked('public_scoreboard'), media_enabled: checked('media_enabled'), obstacle_course_enabled: checked('obstacle_course_enabled'), alcohol_present: checked('alcohol_present'), minors_allowed: checked('minors_allowed'), guardian_required_for_minors: checked('guardian_required_for_minors')
      };
    }

    function hydrateEventForm(event) {
      Object.entries(event).forEach(([key, value]) => {
        const el = q(`[data-field="${key}"]`); if (!el) return;
        if (el.type === 'checkbox') el.checked = !!value;
        else if (el.type === 'datetime-local') el.value = value ? new Date(value).toISOString().slice(0,16) : '';
        else el.value = value ?? '';
      });
      syncConditionalPanels();
    }

    async function addSchedule() {
      const val = (name) => q(`[data-schedule="${name}"]`)?.value || null;
      const item = { event_id: state.event?.id, item_type: val('item_type'), title: val('title'), station_name: val('station_name'), starts_at: toIso(val('starts_at')), ends_at: toIso(val('ends_at')), call_minutes_before: Number(val('call_minutes_before') || 0), status: 'scheduled' };
      if (!item.title || !item.starts_at) return showNotice('Schedule title and start time are required.');
      if (!state.supabase || state.event?.id === 'preview-event') { state.schedule.push({ ...item, id: crypto.randomUUID() }); state.schedule.sort((a,b)=>String(a.starts_at).localeCompare(String(b.starts_at))); renderSchedule(); return; }
      const { data, error } = await state.supabase.from('fs_live_schedule_items').insert(item).select().single();
      if (error) return showNotice(`Schedule save failed: ${error.message}`); state.schedule.push(data); renderSchedule();
    }

    function readScoreConfig() {
      const val = (name) => q(`[data-score-config="${name}"]`)?.value;
      const checked = (name) => !!q(`[data-score-config="${name}"]`)?.checked;
      return { mode: val('mode') || 'points_high', unit: val('unit') || 'points', target: numOrNull(val('target')), attempts: Number(val('attempts') || 1), judges: Number(val('judges') || 1), tiebreak: val('tiebreak') || 'best_attempt', drop_high_low: checked('drop_high_low'), penalties_enabled: checked('penalties_enabled') };
    }

    async function saveScoring() {
      state.scoreConfig = readScoreConfig();
      if (!state.supabase || !state.event?.id || state.event.id === 'preview-event') return showNotice('Scoring rule saved in preview. Connect/load a live event to persist it.');
      const payload = { event_id: state.event.id, name: 'Primary Scoring', mode: state.scoreConfig.mode, unit: state.scoreConfig.unit, configuration: state.scoreConfig, active: true };
      const { error } = await state.supabase.from('fs_live_scoring_rules').upsert(payload, { onConflict: 'event_id,name' });
      if (error) showNotice(`Scoring rule save failed: ${error.message}`); else hideNotice();
    }

    function calculateDemo() {
      state.scoreConfig = readScoreConfig();
      const values = (q('[data-calculator-values]')?.value || '').split(',').map((v) => Number(v.trim())).filter(Number.isFinite);
      const penalty = Number(q('[data-calculator-penalty]')?.value || 0);
      const result = calculate(values, penalty, state.scoreConfig);
      q('[data-calculator-result]').textContent = result.display;
      q('[data-calculator-explanation]').textContent = result.explanation;
    }

    function calculate(values, penalty, config) {
      if (!values.length) return { value: null, display: '—', explanation: 'No valid values supplied.' };
      const mode = config.mode;
      let value; let lowerWins = false;
      if (mode === 'points_high' || mode === 'distance_high' || mode === 'reps_high' || mode === 'wins_high') value = values.reduce((a,b)=>a+b,0) - penalty;
      else if (mode === 'time_low') { value = values.reduce((a,b)=>a+b,0) + penalty; lowerWins = true; }
      else if (mode === 'target_closest') { const target = Number(config.target || 0); const best = values.reduce((a,b)=>Math.abs(b-target)<Math.abs(a-target)?b:a); value = Math.abs(best-target) + penalty; lowerWins = true; }
      else if (mode === 'judged_average') { let scores = [...values]; if (config.drop_high_low && scores.length >= 3) { scores.sort((a,b)=>a-b); scores = scores.slice(1,-1); } value = scores.reduce((a,b)=>a+b,0)/scores.length - penalty; }
      else if (mode === 'best_of_n') value = Math.max(...values) - penalty;
      else if (mode === 'aggregate_n') value = values.reduce((a,b)=>a+b,0) - penalty;
      else value = values.reduce((a,b)=>a+b,0) - penalty;
      const rounded = Math.round(value * 1000) / 1000;
      return { value: rounded, display: `${rounded} ${config.unit || ''}`.trim(), lowerWins, explanation: `${mode.replaceAll('_',' ')} • ${values.length} value(s) • penalty ${penalty || 0}${lowerWins ? ' • lower is better' : ' • higher is better'}` };
    }

    function toggleClock() {
      const button = q('[data-action="start-clock"]');
      if (state.clockTimer) { clearInterval(state.clockTimer); state.clockTimer = null; button.textContent = 'Start Clock'; return; }
      state.clockTimer = setInterval(() => { state.clockSeconds += 1; renderClock(); }, 1000); button.textContent = 'Pause Clock';
    }
    function resetClock() { if (state.clockTimer) { clearInterval(state.clockTimer); state.clockTimer = null; } state.clockSeconds = 0; q('[data-action="start-clock"]').textContent = 'Start Clock'; renderClock(); }
    function renderClock() { const m = String(Math.floor(state.clockSeconds/60)).padStart(2,'0'); const s = String(state.clockSeconds%60).padStart(2,'0'); q('[data-scoreboard-clock]').textContent = `${m}:${s}`; }

    async function refreshScoreboard() {
      if (state.supabase && state.event?.id && state.event.id !== 'preview-event') {
        const { data, error } = await state.supabase.from('fs_live_scoreboard_rows').select('*').eq('event_id', state.event.id).order('rank');
        if (!error) state.scoreboard = data || [];
      }
      renderScoreboard();
    }

    async function pauseEvent() {
      if (!state.event) return;
      state.event.status = state.event.status === 'paused' ? 'live' : 'paused';
      if (state.supabase && state.event.id !== 'preview-event') await state.supabase.from('fs_live_events').update({ status: state.event.status }).eq('id', state.event.id);
      renderHeader();
    }

    async function completeInspection() {
      if (!state.event) return;
      const record = { event_id: state.event.id, inspection_type: 'pre_open', status: 'passed', checklist: defaultSafetyChecks().map((label)=>({label,checked:true})), completed_at: new Date().toISOString() };
      if (state.supabase && state.event.id !== 'preview-event') { const { error } = await state.supabase.from('fs_live_safety_inspections').insert(record); if (error) return showNotice(error.message); }
      q('[data-safety-state]').textContent = 'Pre-open passed'; q('[data-safety-summary]').textContent = 'Pre-open inspection completed. Continue monitoring conditions throughout the event.';
      qa('[data-check="safety"]').forEach((el)=>el.classList.add('is-ready'));
    }

    async function addHazard() {
      const description = q('[data-hazard-note]')?.value.trim(); if (!description) return;
      const record = { event_id: state.event?.id, description, status: 'open', severity: 'moderate' };
      if (state.supabase && state.event?.id !== 'preview-event') { const { error } = await state.supabase.from('fs_live_hazards').insert(record); if (error) return showNotice(error.message); }
      q('[data-hazard-note]').value=''; showNotice('Hazard logged. Assign mitigation and close it only after verification.');
    }

    async function logIncident() {
      const severity = q('[data-incident="severity"]')?.value; const summary = q('[data-incident="summary"]')?.value.trim(); if (!summary) return;
      const record = { event_id: state.event?.id, severity, summary, incident_type: 'operational', status: 'open', occurred_at: new Date().toISOString() };
      if (state.supabase && state.event?.id !== 'preview-event') { const { error } = await state.supabase.from('fs_live_incidents').insert(record); if (error) return showNotice(error.message); }
      q('[data-incident="summary"]').value=''; showNotice('Incident logged. Critical events should also follow the venue emergency action plan.');
    }

    async function sendMessage() {
      if (!state.event) return showNotice('Load or create an event first.');
      const get=(k)=>q(`[data-message="${k}"]`);
      const record={event_id:state.event.id,audience_type:get('audience')?.value||'all',priority:get('priority')?.value||'normal',subject:get('subject')?.value||'',body:get('body')?.value||'',channels:['in_app','email','sms'].filter((c)=>get(c)?.checked),delivery_status:'queued'};
      if (!record.body) return showNotice('Message body is required.');
      if (state.supabase && state.event.id !== 'preview-event') { const { data,error }=await state.supabase.from('fs_live_messages').insert(record).select().single(); if(error)return showNotice(error.message); state.messages.unshift(data); }
      else state.messages.unshift({...record,id:crypto.randomUUID(),created_at:new Date().toISOString()});
      renderMessages(); showNotice('Message queued. Email/SMS delivery requires the optional Edge Function provider configuration.');
    }

    function copyPacketLink() {
      const eventId = state.event?.id || 'EVENT_ID'; const link = `${location.origin}/pages/fanscore-live-contestant?event=${encodeURIComponent(eventId)}`;
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).then(()=>showNotice('Contestant portal link copied.')).catch(()=>showNotice(link)); else showNotice(link);
    }

    function subscribeRealtime(eventId) {
      if (!state.supabase?.channel) return;
      try {
        state.supabase.channel(`fs-live:${eventId}`)
          .on('postgres_changes', { event:'*', schema:'public', table:'fs_live_scoreboard_rows', filter:`event_id=eq.${eventId}` }, refreshScoreboard)
          .on('postgres_changes', { event:'*', schema:'public', table:'fs_live_schedule_items', filter:`event_id=eq.${eventId}` }, () => loadLight('schedule'))
          .subscribe();
      } catch (_) {}
    }

    async function loadLight(kind) {
      if (kind==='schedule') { const { data }=await state.supabase.from('fs_live_schedule_items').select('*').eq('event_id',state.event.id).order('starts_at'); state.schedule=data||[]; renderSchedule(); }
    }

    function renderAll(){ renderHeader(); renderMetrics(); renderSchedule(); renderRoster(); renderStaff(); renderMessages(); renderScoreboard(); renderSafetyChecks(); renderDashboard(); }
    function renderHeader(){ const status=state.event?.status||'draft'; q('[data-event-status]').textContent=status.replaceAll('_',' '); q('[data-event-status-dot]').style.background=status==='live'?'var(--fs-good)':status==='paused'?'var(--fs-danger)':'var(--fs-muted)'; }
    function renderMetrics(){ const checked=state.contestants.filter((c)=>c.check_in_status==='checked_in').length; const complete=state.contestants.filter((c)=>c.packet_status==='complete').length; q('[data-metric-contestants]').textContent=state.contestants.length; q('[data-metric-checked-in]').textContent=checked; q('[data-metric-packets]').textContent=state.contestants.length?`${Math.round(complete/state.contestants.length*100)}%`:'0%'; }
    function renderDashboard(){ q('[data-dashboard-event-name]').textContent=state.event?.name||'No event loaded'; q('[data-dashboard-venue]').textContent=state.event?.venue_name||'—'; q('[data-dashboard-start]').textContent=formatDate(state.event?.starts_at); q('[data-dashboard-format]').textContent=state.event?.competition_format||'—'; q('[data-dashboard-scoring]').textContent=state.scoreConfig.mode.replaceAll('_',' '); const next=state.schedule.find((x)=>new Date(x.starts_at)>new Date()); q('[data-next-schedule]').innerHTML=next?`<strong>${escapeHtml(next.title)}</strong><br>${escapeHtml(formatDate(next.starts_at))}<br>${escapeHtml(next.station_name||'')}`:'No upcoming heat or station assignment.'; qa('[data-check="packet"]').forEach((el)=>el.classList.toggle('is-ready',state.contestants.length>0&&state.contestants.every((c)=>c.packet_status==='complete'))); qa('[data-check="staff"]').forEach((el)=>el.classList.toggle('is-ready',state.staff.some((s)=>s.role==='scorer')&&state.staff.some((s)=>['safety','medical'].includes(s.role)))); qa('[data-check="scoreboard"]').forEach((el)=>el.classList.toggle('is-ready',!!state.event?.public_scoreboard)); }
    function renderSchedule(){ const body=q('[data-schedule-table]'); if(!body)return; body.innerHTML=state.schedule.map((x)=>`<tr><td>${escapeHtml(formatDate(x.starts_at))}</td><td>${escapeHtml(x.item_type||'')}</td><td>${escapeHtml(x.title||'')}</td><td>${escapeHtml(x.station_name||'—')}</td><td><span class="fs-live__badge">${escapeHtml(x.status||'scheduled')}</span></td></tr>`).join('')||'<tr><td colspan="5">No schedule items yet.</td></tr>'; q('[data-schedule-count]').textContent=`${state.schedule.length} items`; }
    function renderRoster(){ const body=q('[data-roster-table]'); if(!body)return; const search=(q('[data-roster-search]')?.value||'').toLowerCase(); const rows=state.contestants.filter((c)=>`${c.public_name||''} ${c.team_name||''} ${c.division_name||''} ${c.bib_number||''}`.toLowerCase().includes(search)); body.innerHTML=rows.map((c)=>`<tr><td>${escapeHtml(c.public_name||'Contestant')}</td><td>${escapeHtml(c.team_name||c.division_name||'—')}</td><td><span class="fs-live__badge ${c.packet_status==='complete'?'fs-live__badge--good':'fs-live__badge--warn'}">${escapeHtml(c.packet_status||'pending')}</span></td><td>${escapeHtml(c.check_in_status||'not checked in')}</td><td>${escapeHtml(c.bib_number||'—')}</td></tr>`).join('')||'<tr><td colspan="5">No contestants found.</td></tr>'; q('[data-roster-count]').textContent=`${state.contestants.length} registered`; }
    function renderStaff(){ const body=q('[data-staff-table]'); if(!body)return; body.innerHTML=state.staff.map((s)=>`<tr><td>${escapeHtml(s.display_name||'Staff')}</td><td>${escapeHtml(s.role||'')}</td><td>${escapeHtml(s.assignment||'All event')}</td><td>${escapeHtml(s.status||'active')}</td></tr>`).join('')||'<tr><td colspan="4">No staff assigned.</td></tr>'; }
    function renderMessages(){ const body=q('[data-message-log]'); if(!body)return; body.innerHTML=state.messages.map((m)=>`<tr><td>${escapeHtml(formatDate(m.created_at))}</td><td>${escapeHtml(m.audience_type||'all')}</td><td>${escapeHtml(m.subject||'(no subject)')}</td><td>${escapeHtml(m.delivery_status||'queued')}</td></tr>`).join('')||'<tr><td colspan="4">No messages yet.</td></tr>'; }
    function renderScoreboard(){ q('[data-scoreboard-event]').textContent=state.event?.name||'FanScore Competition'; const next=state.schedule.find((x)=>['heat','wave','station'].includes(x.item_type)&&x.status!=='complete')||state.schedule[0]; q('[data-scoreboard-current]').textContent=next?.title||'Waiting for event'; q('[data-scoreboard-station]').textContent=next?.station_name||'—'; const list=q('[data-scoreboard-rows]'); list.innerHTML=state.scoreboard.map((r,i)=>`<li><strong>${escapeHtml(String(r.rank||i+1))}</strong><span>${escapeHtml(r.display_label||'Contestant')}</span><strong>${escapeHtml(String(r.display_score??r.score_value??'—'))}</strong></li>`).join('')||'<li><strong>—</strong><span>Scores will appear here</span><strong>—</strong></li>'; }
    function renderSafetyChecks(){ const holder=q('[data-safety-checks]'); if(holder)holder.innerHTML=defaultSafetyChecks().map((x,i)=>`<label><input type="checkbox" data-safety-check="${i}"> ${escapeHtml(x)}</label>`).join(''); }
    function defaultSafetyChecks(){ return ['Emergency action plan posted','Medical/first aid coverage confirmed','Course/stations inspected','Required protective equipment available','Weather/venue conditions checked','Staff radio/communications checked','Stop-work signal briefed']; }
    function syncConditionalPanels(){ const alcohol=!!q('[data-field="alcohol_present"]')?.checked; q('[data-alcohol-callout]').hidden=!alcohol; q('[data-alcohol-safety-panel]').hidden=!alcohol; q('[data-alcohol-packet]').hidden=!alcohol; const guardians=!!q('[data-field="minors_allowed"]')?.checked&&!!q('[data-field="guardian_required_for_minors"]')?.checked; q('[data-guardian-packet]').hidden=!guardians; }

    function seedPreview(){ if(state.event)return; state.event={id:'preview-event',name:'',slug:'',venue_name:'',timezone:'America/New_York',starts_at:null,ends_at:null,competition_format:'individual',capacity:100,status:'draft',public_scoreboard:false,media_enabled:true,obstacle_course_enabled:false,alcohol_present:false,minors_allowed:true,guardian_required_for_minors:true}; hydrateEventForm(state.event); state.schedule=[]; state.contestants=[]; state.staff=[]; state.scoreboard=[]; state.messages=[]; }

    function exportCompliance(){ exportCsv('fanscore-live-compliance.csv',state.contestants.map((c)=>({contestant:c.public_name,packet_status:c.packet_status,check_in_status:c.check_in_status}))); }
    function exportOperations(){ exportCsv('fanscore-live-operations.csv',state.schedule.map((x)=>({type:x.item_type,title:x.title,station:x.station_name,start:x.starts_at,status:x.status}))); }
    function exportCsv(filename, rows){ if(!rows?.length)return showNotice('Nothing to export yet.'); const keys=[...new Set(rows.flatMap((r)=>Object.keys(r).filter((k)=>typeof r[k]!=='object')) )]; const csv=[keys.join(','),...rows.map((r)=>keys.map((k)=>csvCell(r[k])).join(','))].join('\n'); const blob=new Blob([csv],{type:'text/csv'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url;a.download=filename;a.click();URL.revokeObjectURL(url); }
    function csvCell(v){ const s=String(v??'').replaceAll('"','""'); return `"${s}"`; }
    function showNotice(text){ notice.textContent=text;notice.hidden=false; }
    function hideNotice(){ notice.hidden=true; }
    function slugify(v){ return v.toLowerCase().trim().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,''); }
    function toIso(v){ if(!v)return null; const d=new Date(v); return Number.isNaN(d.getTime())?null:d.toISOString(); }
    function numOrNull(v){ const n=Number(v); return Number.isFinite(n)&&v!==''?n:null; }
    function formatDate(v){ if(!v)return'—'; const d=new Date(v); return Number.isNaN(d.getTime())?String(v):d.toLocaleString([], {month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}); }
    function escapeHtml(v){ return String(v??'').replace(/[&<>'"]/g,(c)=>({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#039;','"':'&quot;'}[c])); }
  }
})();
